import { Box, Divider, MenuList, SvgIcon, Typography } from '@mui/material';
import React, { MouseEvent } from 'react';

import { PopperSubmenuContainer } from '../../Components/PopperSubmenuContainer';
import { NAV_COLOR_PALETTE } from '../../Components/styleConstants';
import { delayedMobileCloseClickHandler } from '../../Components/utils';
import { DATA_TEST, getDynamicDataTestId } from '../../enums';
import { BadgeVariant, UtilityNames, UtilityTrayMenuItem, UtilityTrayMenuSection } from '../../types';

import { TrayControllerButton } from './TrayControllerButton';
import { TrayIconLink } from './TrayIconLink';
import { TrayMenuItem } from './TrayMenuItem';

interface ITrayOptionProps {
  utilityName: UtilityNames | string;
  muiIcon?: typeof SvgIcon;
  stringIcon?: string;
  menuSections?: UtilityTrayMenuSection[];
  iconLinkComponent?: React.ElementType;
  iconUrl?: string;
  badgeContent?: number;
  badgeVariant?: BadgeVariant;
  iconOnClickOverride?(event: MouseEvent<HTMLElement>): void;
}

interface ITrayMenuSectionCompProps {
  sectionName: string;
  menuItems: UtilityTrayMenuItem[];
  anchorEl: HTMLElement;
  divider: boolean;
  firstTrayMenuItemRef: React.RefObject<HTMLLIElement>;
  handleClose(): void;
}

const TrayMenuSectionComp: React.FC<ITrayMenuSectionCompProps> = ({
  sectionName,
  menuItems,
  divider,
  anchorEl,
  firstTrayMenuItemRef,
  handleClose,
  // https://github.com/mui/material-ui/issues/35786
  // MUI's MenuItem props are passed down to each direct child automatically
  // We can therefore hook into the native behavior of 'autoFocus' and avoid handling it ourselves
  ...muiMenuItemProps
}) => {
  const { autoFocus: autoFocusFirstItem } = muiMenuItemProps as { autoFocus: boolean; tabIndex: number };
  return (
    <>
      {sectionName && (
        <Typography
          px={2}
          py={1}
          variant="h5"
          component="li"
          sx={{ color: NAV_COLOR_PALETTE.primaryGray, lineHeight: '1.25rem' }}
        >
          {sectionName}
        </Typography>
      )}
      {menuItems?.map((menuItem, idx) => (
        <TrayMenuItem
          dataTest={getDynamicDataTestId(
            menuItem.url ? DATA_TEST.utility_tray_menu_item_link : DATA_TEST.utility_tray_menu_item,
            menuItem.label
          )}
          dataOg={menuItem.dataOg}
          autoFocus={idx === 0 && autoFocusFirstItem}
          firstTrayMenuItemRef={firstTrayMenuItemRef}
          {...menuItem}
          key={menuItem.label}
          onClick={() => {
            menuItem.onClick?.();
            handleClose();
          }}
          anchorEl={anchorEl}
        />
      ))}
      {divider && <Divider aria-hidden={true} component="li" />}
    </>
  );
};

export const TrayOption = ({
  utilityName,
  muiIcon,
  stringIcon,
  menuSections = [],
  iconLinkComponent,
  iconUrl,
  iconOnClickOverride,
  badgeContent,
  badgeVariant
}: ITrayOptionProps) => {
  const controllerButtonRef = React.useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const firstTrayMenuItemRef = React.useRef<HTMLLIElement>(null);
  const customMuiIcon = muiIcon;

  const handleClose = () => {
    setMenuOpen(false);
  };

  const controllerId = `${utilityName}-button`;
  const associatedMenuId = `${utilityName}-menu`;

  return (
    <Box data-test={getDynamicDataTestId(DATA_TEST.tray_option_container, utilityName)}>
      {iconUrl && (
        <TrayIconLink
          utilityName={utilityName}
          customMuiIcon={customMuiIcon}
          stringIcon={stringIcon}
          url={iconUrl}
          badgeContent={badgeContent}
          badgeVariant={badgeVariant}
          linkComponent={iconLinkComponent}
          onClick={(e: MouseEvent<HTMLElement>) => {
            if (iconOnClickOverride) {
              iconOnClickOverride(e);
            }
          }}
        />
      )}
      {!iconUrl && (
        <TrayControllerButton
          ref={controllerButtonRef}
          utilityName={utilityName}
          customMuiIcon={customMuiIcon}
          stringIcon={stringIcon}
          id={controllerId}
          associatedMenuId={associatedMenuId}
          onClick={(e: MouseEvent<HTMLElement>) => {
            if (iconOnClickOverride) {
              iconOnClickOverride(e);
              return;
            }
            if (menuSections?.length > 0) setMenuOpen((o) => !o);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && firstTrayMenuItemRef?.current) {
              firstTrayMenuItemRef.current?.focus();
            }
          }}
          expanded={menuOpen}
          badgeContent={badgeContent}
          badgeVariant={badgeVariant}
        />
      )}
      <PopperSubmenuContainer
        anchorEl={controllerButtonRef.current}
        open={menuOpen}
        positionOffset={{ x: 0, y: 13 }}
        handleClose={() => {
          delayedMobileCloseClickHandler(handleClose);
        }}
        dataTest={DATA_TEST.icon_utility_submenu}
        placement="bottom-end"
      >
        <MenuList sx={{ maxWidth: 200 }} id={associatedMenuId} autoFocusItem={menuOpen}>
          {menuSections.map((section, idx, array) => {
            return (
              <TrayMenuSectionComp
                firstTrayMenuItemRef={firstTrayMenuItemRef}
                sectionName={section.label}
                menuItems={section.menuItems}
                key={section.label || JSON.stringify(section.menuItems)}
                // Don't show divider for the last section
                divider={idx !== array.length - 1}
                handleClose={handleClose}
                anchorEl={controllerButtonRef.current}
              />
            );
          })}
        </MenuList>
      </PopperSubmenuContainer>
    </Box>
  );
};
